import { useCallback, useEffect, useRef, useState } from "react";

type OAuthMessage = {
  type: "shopify_oauth_callback";
  query?: string | null;
  state?: string | null;
  error?: string | null;
};

type Opts = {
  /**
   * Return the state token the opener stored when opening the popup. Incoming
   * postMessages whose `state` doesn't match are ignored (another wizard/tab
   * owns that callback). Returning `null` accepts any state.
   */
  getExpectedState: () => string | null;
  /** Called with the raw Shopify callback querystring the popup relayed. */
  onComplete: (args: { query: string }) => void | Promise<void>;
  onError?: (message: string) => void;
};

/**
 * Shopify OAuth popup flow — mirrors useFacebookOAuthPopup. The opener opens the
 * consent popup; the FRONTEND landing page (/auth/shopify/callback) posts the RAW
 * callback querystring back here, which the opener hands to the backend exchange
 * (the exchange verifies the HMAC over that exact query). Origin is strictly
 * checked; the completion handler fires at most once per popup open.
 */
export function useShopifyOAuthPopup({ getExpectedState, onComplete, onError }: Opts) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const completedRef = useRef(false);

  const reportError = useCallback(
    (msg: string) => {
      setError(msg);
      setLoading(false);
      onError?.(msg);
    },
    [onError],
  );

  // Shared handling for both channels: the window `message` event (postMessage
  // from the opener) and the BroadcastChannel (fallback for when Shopify's
  // COOP-setting login page severs window.opener). `completedRef` de-dupes so
  // the exchange fires at most once even if both channels deliver.
  const processPayload = useCallback(
    async (data: OAuthMessage | undefined) => {
      if (!data || data.type !== "shopify_oauth_callback") return;

      const expected = getExpectedState();
      if (expected !== null && data.state !== expected) return;

      popupRef.current = null;

      if (data.error) {
        reportError(`Shopify authorization failed: ${data.error}`);
        return;
      }
      if (!data.query) {
        reportError("No response received from Shopify. Please try again.");
        return;
      }

      if (completedRef.current) return;
      completedRef.current = true;

      try {
        await onComplete({ query: data.query });
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Failed to complete the Shopify connection.";
        reportError(msg);
        return;
      }

      setLoading(false);
    },
    [getExpectedState, onComplete, reportError],
  );

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // postMessage is cross-origin-capable, so the origin MUST be checked.
      if (event.origin !== window.location.origin) return;
      void processPayload(event.data as OAuthMessage | undefined);
    },
    [processPayload],
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    // BroadcastChannel is same-origin by spec (no origin check needed) and
    // survives the popup losing its opener.
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel("shopify_oauth");
      bc.onmessage = (e) => void processPayload(e.data as OAuthMessage | undefined);
    } catch {
      /* BroadcastChannel unsupported — postMessage path still applies */
    }
    return () => {
      window.removeEventListener("message", handleMessage);
      bc?.close();
    };
  }, [handleMessage, processPayload]);

  /** Open the Shopify consent popup at `url`. Returns false if the browser blocked it. */
  const openPopup = useCallback(
    (url: string): boolean => {
      completedRef.current = false;
      setError(null);
      setLoading(true);

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      popupRef.current = window.open(
        url,
        "shopify_oauth",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
      );

      if (!popupRef.current) {
        reportError(
          "Popup was blocked by your browser. Please allow popups for this site and try again.",
        );
        return false;
      }

      const interval = setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(interval);
          setLoading((prev) => (prev ? false : prev));
        }
      }, 1000);

      return true;
    },
    [reportError],
  );

  return { openPopup, loading, error, clearError: () => setError(null) };
}
